import React, { useRef } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-icons/tb';

import { useTranslation } from '../../../translations';
import { SignaturePositionSchemaDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { SignatureFrame } from '../../../common/pokCore/validation/signatureFrame';

import {
  fitSignaturePositionToPage,
  getFrameRectPercent,
  PdfPageSize,
} from './signatureFrameGeometry';
import { PreviewImage } from './useSignaturePreviewImages';
import { SignaturePreviewBox } from './SignaturePreviewBox';

interface SignatureFrameMarkerProps {
  number: number;
  position: SignaturePositionSchemaDto;
  pageSize: PdfPageSize;
  draft: boolean;
  highlighted: boolean;
  saving: boolean;
  onMove: (x: number, y: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onHoverChange: (hovered: boolean) => void;
  onSelect?: () => void;
  signatureImage?: PreviewImage;
  stampImage?: PreviewImage;
}

export const SignatureFrameMarker: React.FC<SignatureFrameMarkerProps> = ({
  number,
  position,
  pageSize,
  draft,
  highlighted,
  saving,
  onMove,
  onConfirm,
  onCancel,
  onHoverChange,
  onSelect,
  signatureImage,
  stampImage,
}) => {
  const { t, tk } = useTranslation('commonLng');
  const dragStart = useRef<{
    pointerX: number;
    pointerY: number;
    x: number;
    y: number;
    pageWidthPx: number;
    pageHeightPx: number;
  } | null>(null);

  const rect = getFrameRectPercent(position.x, position.y, pageSize);
  const selectable = !draft && !!onSelect;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!draft || saving) {
      return;
    }
    const pageElement = event.currentTarget.parentElement;
    if (!pageElement) {
      return;
    }
    const pageRect = pageElement.getBoundingClientRect();
    dragStart.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: position.x,
      y: position.y,
      pageWidthPx: pageRect.width,
      pageHeightPx: pageRect.height,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    if (!start) {
      return;
    }
    const dx = ((event.clientX - start.pointerX) / start.pageWidthPx) * 100;
    const dy = ((event.clientY - start.pointerY) / start.pageHeightPx) * 100;
    const next = fitSignaturePositionToPage(
      start.x + dx,
      start.y + dy,
      pageSize,
    );
    onMove(next.x, next.y);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStart.current) {
      dragStart.current = null;
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const classNames = [
    'signature-frame',
    draft ? 'signature-frame--draft' : 'signature-frame--confirmed',
    highlighted ? 'signature-frame--highlighted' : '',
    selectable ? 'signature-frame--selectable' : '',
  ].join(' ');

  const selectProps = selectable
    ? {
        tabIndex: 0,
        onClick: onSelect,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect?.();
          }
        },
      }
    : {};

  const frame = (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classNames}
      style={{
        left: `${rect.left}%`,
        top: `${rect.top}%`,
        width: `${rect.width}%`,
        height: `${rect.height}%`,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      // Never let the pan mode of the viewer start under the frame.
      onMouseDown={event => event.stopPropagation()}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      role={selectable ? 'button' : 'img'}
      {...selectProps}
    >
      <SignaturePreviewBox
        image={signatureImage}
        leftPt={0}
        widthPt={SignatureFrame.signatureBoxWidthPt}
      />
      <SignaturePreviewBox
        image={stampImage}
        leftPt={SignatureFrame.signatureBoxWidthPt + SignatureFrame.gapPt}
        widthPt={SignatureFrame.stampBoxWidthPt}
      />
      <span className="signature-frame__sample">
        {t(tk.pdfViewer.signatureSample)}
      </span>
      <span className="signature-frame__number">{number}</span>
      {draft && (
        <div className="signature-frame__actions">
          <OverlayTrigger
            overlay={<Tooltip>{t(tk.pdfViewer.confirmSignature)}</Tooltip>}
          >
            <button
              type="button"
              className="signature-frame__action signature-frame__action--confirm"
              onPointerDown={event => event.stopPropagation()}
              onClick={onConfirm}
              disabled={saving}
            >
              <Icon.TbCheck size={14} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            overlay={<Tooltip>{t(tk.pdfViewer.cancelSignature)}</Tooltip>}
          >
            <button
              type="button"
              className="signature-frame__action signature-frame__action--cancel"
              onPointerDown={event => event.stopPropagation()}
              onClick={onCancel}
              disabled={saving}
            >
              <Icon.TbX size={14} />
            </button>
          </OverlayTrigger>
        </div>
      )}
    </div>
  );

  return selectable ? (
    <OverlayTrigger
      overlay={<Tooltip>{t(tk.pdfViewer.clickToMoveSignature)}</Tooltip>}
    >
      {frame}
    </OverlayTrigger>
  ) : (
    frame
  );
};
